import { useState } from "react";
import { Card } from "react-bootstrap";
import { Button, Icon } from "@aidkitorg/component-library";
import { snakeToEnglish, copyToClipboard } from "../Util";
import { useLocalizedStrings } from "../Localization";

type ViewMode = "formatted" | "json";

export type FieldFormatting = { field: string; format: "dollars" | "cents" | "date" | "percentage" };

export type JSONViewerProps = {
  value: object | unknown[];
  className?: string;
  fieldFormatting?: FieldFormatting[];
  title?: React.ReactNode;
  children?: React.ReactNode;
};

function isPrimitive(value: unknown): value is string | number | boolean | null | undefined {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function formatPrimitive(key: string, value: unknown, fieldFormatting?: FieldFormatting[]): string {
  if (value === null) return "--";
  if (value === undefined) return "undefined";
  const config = fieldFormatting?.find((f) => f.field === key);
  if (config) {
    if ((config.format === "dollars" || config.format === "cents") && typeof value === "number") {
      const dollars = config.format === "dollars" ? value : value / 100;
      return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(dollars);
    }
    if (config.format === "percentage" && typeof value === "number") {
      const ratio = value < 1 ? value : value / 100;
      return new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 2 }).format(ratio);
    }
    if (config.format === "date") {
      const date = typeof value === "number" ? new Date(value) : typeof value === "string" ? new Date(value) : null;
      if (date && !isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
      }
    }
  }
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return String(value);
}

function RenderValue({ value, fieldFormatting }: { value: unknown; fieldFormatting?: FieldFormatting[] }) {
  if (Array.isArray(value)) {
    return <RenderArray items={value} fieldFormatting={fieldFormatting} />;
  }
  if (typeof value === "object" && value !== null) {
    return <RenderBlock value={value as Record<string, unknown>} fieldFormatting={fieldFormatting} />;
  }
  return null;
}

const DEFAULT_UL_CLASS = "list-disc pl-4 space-y-1.5 text-sm text-gray-600";

/** Renders an array: empty as [], all-primitive inline with commas, otherwise as a bulleted list. */
function RenderArray({
  items,
  fieldFormatting,
  ulClassName = DEFAULT_UL_CLASS,
  primitiveKey = "",
}: {
  items: unknown[];
  fieldFormatting?: FieldFormatting[];
  ulClassName?: string;
  primitiveKey?: string;
}) {
  if (items.length === 0) {
    return <span className="text-gray-500 text-sm">[]</span>;
  }
  if (items.every(isPrimitive)) {
    return (
      <span className="text-gray-600 text-sm">
        {items.map((item) => formatPrimitive(primitiveKey, item, fieldFormatting)).join(", ")}
      </span>
    );
  }
  return (
    <ul className={ulClassName}>
      {items.map((item, i) =>
        isPrimitive(item) ? (
          <li key={i}>{formatPrimitive("", item, fieldFormatting)}</li>
        ) : (
          <li key={i}>
            <RenderValue value={item} fieldFormatting={fieldFormatting} />
          </li>
        )
      )}
    </ul>
  );
}

/** Renders an object as a list of key-value pairs. */
function RenderBlock({
  value,
  fieldFormatting,
  isTopLevel = false,
}: {
  value: Record<string, unknown>;
  fieldFormatting?: FieldFormatting[];
  isTopLevel?: boolean;
}) {
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <span className="text-gray-500 text-sm">--</span>;
  }
  return (
    <div>
      {entries.map(([k, v]) => (
        <RenderEntry key={k} label={k} value={v} isTopLevel={isTopLevel} fieldFormatting={fieldFormatting} />
      ))}
    </div>
  );
}

/** Renders a single labeled key-value pair, delegating nested structures to RenderArray / RenderBlock. */
function RenderEntry({
  label,
  value,
  isTopLevel = false,
  fieldFormatting,
}: {
  label: string;
  value: unknown;
  isTopLevel?: boolean;
  fieldFormatting?: FieldFormatting[];
}) {
  const hasNested = !isPrimitive(value);
  const labelClass = isTopLevel
    ? "text-base font-bold text-gray-800 shrink-0"
    : "font-medium text-sm text-gray-800 shrink-0";

  if (isPrimitive(value)) {
    return (
      <div className="flex flex-wrap gap-x-2 py-0.5 text-gray-700 text-sm">
        <span className={labelClass}>{snakeToEnglish(label)}:</span>
        <span className="text-gray-600 break-all">
          {typeof value === "string" && (value === "" || value.includes("\n") || value.length > 60) ? (
            <span className="whitespace-pre-wrap">{value}</span>
          ) : (
            formatPrimitive(label, value, fieldFormatting)
          )}
        </span>
      </div>
    );
  }

  if (Array.isArray(value)) {
    const useInline = value.length === 0 || value.every(isPrimitive);
    return (
      <div className={useInline ? "flex flex-wrap gap-x-2 py-0.5 text-gray-700 text-sm" : "py-0.5"}>
        <span className={labelClass}>{snakeToEnglish(label)}:</span>
        {useInline ? (
          <span className="text-gray-600">
            <RenderArray items={value} fieldFormatting={fieldFormatting} primitiveKey={label} />
          </span>
        ) : (
          <RenderArray
            items={value}
            fieldFormatting={fieldFormatting}
            ulClassName="mt-0.5 ml-2 list-disc pl-4 space-y-1.5 border-l-2 border-gray-200 pl-2 text-gray-600 text-sm"
          />
        )}
      </div>
    );
  }

  if (typeof value === "object" && value !== null) {
    return (
      <div className="py-0.5">
        <span className={labelClass}>{snakeToEnglish(label)}:</span>
        <div className="border-l-2 border-gray-200 ml-2 mt-0.5 pl-2">
          <RenderBlock value={value as Record<string, unknown>} fieldFormatting={fieldFormatting} />
        </div>
      </div>
    );
  }

  return null;
}

function ViewModeToggles({
  viewMode,
  onViewModeChange,
  onCopy,
  className = "",
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onCopy: () => void;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const L = useLocalizedStrings();

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs">
        <button
          type="button"
          onClick={() => onViewModeChange("formatted")}
          className={`rounded px-2 py-1 ${
            viewMode === "formatted"
              ? "bg-white font-medium text-gray-800 shadow-sm"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          {L.questions.json_viewer.formatted}
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange("json")}
          className={`rounded px-2 py-1 ${
            viewMode === "json"
              ? "bg-white font-medium text-gray-800 shadow-sm"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          {L.questions.json_viewer.json}
        </button>
      </div>
      <Button
        icon={<Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"} />}
        variant="secondary"
        size="sm"
        aria-label="Copy JSON to clipboard"
        onClick={() => {
          onCopy();
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      />
    </div>
  );
}

export function JSONViewer({ value, className = "", title, children, fieldFormatting }: JSONViewerProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("formatted");

  const formattedContent = Array.isArray(value)
    ? <RenderArray items={value} fieldFormatting={fieldFormatting} />
    : <RenderBlock value={value as Record<string, unknown>} fieldFormatting={fieldFormatting} isTopLevel />;

  return (
    <Card className={className}>
      <Card.Header as="h6" className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <span>{title ?? ''}</span>
        <ViewModeToggles
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onCopy={() => copyToClipboard(JSON.stringify(value, null, 2))}
        />
      </Card.Header>
      <Card.Body as="div" className="alert alert-light mb-0">
        <div className="text-gray-600 whitespace-pre-wrap">
          {viewMode === "formatted" ? formattedContent : (
            <pre className="text-gray-700 text-sm whitespace-pre-wrap break-words font-mono m-0">
              {JSON.stringify(value, null, 2)}
            </pre>
          )}
          {children}
        </div>
      </Card.Body>
    </Card>
  );
}
